import axios from 'axios';

import { useQuery } from '@tanstack/react-query';

import { protectApiViaOkta } from './protectApiViaOkta';

import { OKTA_CLIENT_ID } from '../components/AppV2/RoutesV2';
import { isV2PermissionsEnabled } from '../helpers/featureFlags/isV2PermissionsEnabled';

export const hasAnyRole = (roles = []) => {
  const userInfo = JSON.parse(sessionStorage.getItem('userInfo'));

  return roles.some((role) => userInfo?.roleAssociations?.some((r) => r.code === role));
};

export const useUserInfo = () => {
  const userEmail = JSON.parse(
    localStorage.getItem(`okta-token-storage-mkii-${OKTA_CLIENT_ID}`),
  )?.idToken?.claims?.email?.toLowerCase();

  const { data: userInfo, isFetching: userInfoLoading } = useQuery({
    queryKey: ['userInfo', userEmail],
    queryFn: async () => {
      try {
        const response = await protectApiViaOkta(
          'post',
          process.env.REACT_APP_PERMISSION_LAMBDA_URL,
          { email: userEmail },
        );

        if (response.status === 200) {
          const data = response.data.data;

          sessionStorage.setItem('userInfo', JSON.stringify(data));

          return data;
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        throw error;
      }
    },
    retry: (failureCount, error) => {
      const errorStatuses = [400, 401, 403, 404];

      if (errorStatuses.includes(error.response?.status)) {
        return false;
      }

      return failureCount < 3;
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: isV2PermissionsEnabled() && Boolean(userEmail),
    onSuccess: () => {
      console.log('Successfully fetched data from RBAC');

      return;
    },
  });

  return {
    userInfo,
    userInfoLoading,
  };
};

export const useHasAnyRole = (roles = []) => {
  const { userInfo, userInfoLoading } = useUserInfo();
  const cachedUserInfo = JSON.parse(sessionStorage.getItem('userInfo'));
  const finalUserInfo = userInfo || cachedUserInfo;

  const hasRoles = roles.some((role) =>
    finalUserInfo?.roleAssociations?.some((r) => r.code === role),
  );

  return {
    hasRoles,
    isLoading: userInfoLoading,
    userInfo: finalUserInfo,
  };
};

export const ROLE = {
  SUPER_ADMIN: 'FAST_MK2_SUPER_ADMIN',
  ADMIN: 'FAST_MK2_ADMIN',
  REGIONAL_LEAD: 'FAST_MK2_REGIONAL_LEAD',
  ADVANCED_FLEXPERT_SUPPORT: 'FAST_MK2_ADVANCED_FLEXPERT_SUPPORT',
  FLEXPERT: 'FAST_MK2_FLEXPERT',
  FLEXPERT_SUPPORT: 'FAST_MK2_FLEXPERT_SUPPORT',
  BROKER: 'FAST_MK2_BROKER',
  TRANSACTIONAL_MANAGER: 'FAST_MK2_TRANSACTION_MANAGER',
  CLIENT: 'FAST_MK2_CLIENT',
  BASIC_PROVIDER: 'FAST_MK2_BASIC_PROVIDER',
  ADVANCED_PROVIDER: 'FAST_MK2_ADVANCED_PROVIDER',
};

export const transactionsAllowedRoles = [
  'FAST_MK2_SUPER_ADMIN',
  'FAST_MK2_ADMIN',
  'FAST_MK2_REGIONAL_LEAD',
  'FAST_MK2_ADVANCED_FLEXPERT_SUPPORT',
  'FAST_MK2_FLEXPERT',
  'FAST_MK2_FLEXPERT_SUPPORT',
  'FAST_MK2_BROKER',
  'FAST_MK2_TRANSACTION_MANAGER',
  'FAST_MK2_CLIENT',
];

export const analyticsAllowedRoles = [
  'FAST_MK2_SUPER_ADMIN',
  'FAST_MK2_ADMIN',
  'FAST_MK2_REGIONAL_LEAD',
  'FAST_MK2_ADVANCED_FLEXPERT_SUPPORT',
  'FAST_MK2_FLEXPERT',
  'FAST_MK2_FLEXPERT_SUPPORT',
  'FAST_MK2_BROKER',
  'FAST_MK2_TRANSACTION_MANAGER',
  'FAST_MK2_CLIENT',
];

export const providerLocationsAllowedRoles = [
  'FAST_MK2_SUPER_ADMIN',
  'FAST_MK2_ADMIN',
  'FAST_MK2_REGIONAL_LEAD',
  'FAST_MK2_ADVANCED_FLEXPERT_SUPPORT',
  'FAST_MK2_FLEXPERT',
  'FAST_MK2_FLEXPERT_SUPPORT',
  'FAST_MK2_BROKER',
  'FAST_MK2_BASIC_PROVIDER',
  'FAST_MK2_ADVANCED_PROVIDER',
];

export const requestSurveyAllowedRoles = [
  'FAST_MK2_SUPER_ADMIN',
  'FAST_MK2_ADMIN',
  'FAST_MK2_REGIONAL_LEAD',
  'FAST_MK2_ADVANCED_FLEXPERT_SUPPORT',
  'FAST_MK2_FLEXPERT',
  'FAST_MK2_FLEXPERT_SUPPORT',
  'FAST_MK2_BROKER',
  'FAST_MK2_TRANSACTION_MANAGER',
];
